// The property's STATUS card — market reports (0258), our own close (DealClosePanel), reopen, and
// the availability log. Moved out of LineageModal.jsx on 2026-09-16 so the valuation tab can mount
// the SAME component: agents on a call were typing „გაიყიდა" and „არ თანამშრომლობს" into comments
// because the deal editor covers the property card, and these buttons were only on the card.
// One component, two doors — never a second set of buttons writing the same facts.
import { useEffect, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { card, btn, COUNTER, ROW, SECTION_LABEL, pill, timeAgo } from './primitives.jsx'
import { DealClosePanel } from './DealCloseModal.jsx'
import { getMarketReports, recordMarketReport, reopenListing, retractMarketReport, recordConfirmedDeal } from '../../db/core.js'

// ─── Availability history (0304/0305) — the close is a DATED FACT, not a status ─────────────
// Owner question 2026-07-30: "how do we show it went inactive → active again, and how do we mark
// that the close is history now, not status?" It always WAS history: listing_state_event is an
// append-only log and listing_state_current is only its reduction. Nothing had ever shown the
// log, so one reduced value read as a permanent label. This renders the log.
//
// Retracted rows stay VISIBLE, struck through. A close withdrawn as "recorded by mistake" must
// remain legible as something written and then withdrawn — hiding it would make the correction
// itself untraceable, which is the failure AD-3 exists to prevent.
const LIFECYCLE_LABEL = {
  status_active: 'desk_lifecycle_active', status_sold: 'desk_entry_closed_sold',
  status_rented_out: 'desk_entry_closed_rented', status_withdrawn: 'desk_lifecycle_withdrawn',
  status_reserved: 'desk_lifecycle_reserved', status_negotiating: 'desk_lifecycle_negotiating',
  status_paused: 'desk_lifecycle_paused',
}
function LifecycleHistory({ history }) {
  const [open, setOpen] = useState(false)
  const rows = (history || []).filter(h => LIFECYCLE_LABEL[h.value])
  // One row is not a history — it is just the current state, already shown as the pill above.
  if (rows.length < 2) return null
  const shown = open ? rows : rows.slice(0, 2)
  return (
    <div className="mt-2 pt-2 border-t border-border/40 space-y-0.5">
      {shown.map((h, i) => (
        <div key={i} className={`flex items-baseline justify-between gap-2 text-xs leading-[1.45] ${h.invalid ? 'opacity-50' : ''}`}>
          <span className={`min-w-0 truncate ${h.invalid ? 'line-through' : ''} ${
            i === 0 && !h.invalid ? 'text-foreground' : 'text-muted-foreground'}`}>
            {t(LIFECYCLE_LABEL[h.value])}
            {/* WHO said so is the point of a log. A scrape has no person behind it and must not
                be given one — "the platform said so" is the honest reading. */}
            <span className="text-muted-foreground/70">
              {' · '}{h.by || t(`desk_lifecycle_src_${h.source_kind}`)}
            </span>
            {h.invalid && <span className="ml-1">({t('desk_lifecycle_retracted')})</span>}
          </span>
          <span className="shrink-0 text-muted-foreground tabular-nums">{timeAgo(h.observed_at)}</span>
        </div>
      ))}
      {rows.length > 2 && (
        <button type="button" className="text-xs text-primary" onClick={() => setOpen(o => !o)}>
          {open ? t('desk_lifecycle_less') : tLog('desk_lifecycle_more', { n: rows.length - 2 })}
        </button>
      )}
    </div>
  )
}

// ─── Market report (0258) — "I called and the owner says it is gone" ─────────
// Owner ruling 2026-07-29: *"if an agent calls for cooperation and they are told
// it's rented out, they have to be able to save this info."* Lives on the LISTING
// because that is where the agent opens the ad and calls from.
//
// ⚠️ NOT cooperation-gated and NOT participant-gated — that is the whole feature.
// The knowledge is about the PROPERTY; requiring paperwork first is how the agency
// loses it and everyone keeps ringing the same owner. Anti-abuse is 0178's
// claims-vs-facts design, which is stronger than a permission: these reasons are
// UNVERIFIED, so a report lowers visibility and accumulates but NEVER deletes, and
// the SQL allows one live report per (property, agent, reason) so a single agent
// cannot inflate the count. One agent can never remove a rival's property — only
// add their name to a tally everyone can see.
const MARKET_REASONS = ['deal_done_by_other', 'owner_exclusive_elsewhere', 'owner_declined_agent']

// REOPEN — the two reasons a close comes undone. Kept separate on purpose (0304): they differ
// in whether the deal HAPPENED, and therefore in whether the agent keeps the ranking credit.
// One button for both would either fabricate a market event or silently confiscate credit.
const REOPEN_REASONS = ['deal_fell_through', 'recorded_by_mistake']

// closedListingId: the ad the closure was actually RECORDED against, which is not necessarily
// the ad this modal is anchored on — `lifecycle` here is a projection across the whole family
// (AD-1: a closure is keyed to one chosen ad). Reopening the anchor instead would 409 with
// "no recorded close to reopen" on every property whose closed ad is a sibling. Own-audit
// catch, 2026-07-30.
export function MarketReportPanel({ propertyId, agentId, agentName = null, listingId = null,
                             lifecycle = null, closedListingId = null,
                             lifecycleHistory = null, contested = false, onDealClosed = null }) {
  const [reopening, setReopening] = useState(false)
  const [reopenReason, setReopenReason] = useState(null)
  const [data, setData] = useState(null)      // { reports, report_count }; null = loading
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [saved, setSaved] = useState(false)
  const [noteVal, setNoteVal] = useState('')  // optional free-text detail riding the report (0258 `note`)
  // The picked reason is now STATE, not the click that submits (§3c) — see the form below
  // for why: reading noteVal at reason-click time silently dropped the comment.
  const [reason, setReason] = useState(null)
  // KNOWN DEAL (statistics line, owner 2026-10-07: "yes we need that" / "do that"). When the agent reports that someone
  // ELSE closed the deal and learned its price, that price is the most valuable fact on this card: every other price we
  // hold is an asking price. Optional, under the 'deal_done_by_other' reason only; Save records the report first (as
  // always) and then the deal (core confirmed_deal_record_v1). The report never depends on the deal: a refused price
  // leaves the report saved and says why.
  const [known, setKnown] = useState({ deal: 'sale', price: '', currency: 'USD', date: '' })
  const [knownSaved, setKnownSaved] = useState(false)
  // OUR OWN close is not a market report (owner 2026-07-30) — it expands the same
  // DealClosePanel the meeting's "closed" pill expands: final price + commission,
  // confirmed, cooperation ended as deal_closed. One panel, two doors.
  const [closeDeal, setCloseDeal] = useState(false)
  // A recorded close makes the report REASONS meaningless (owner-caught 2026-07-30: "if
  // rented out is selected, is there any logic in those other options?" — no). All three
  // reasons describe why an AVAILABLE property isn't reachable through us; a closed deal
  // answers that question with a settled fact, so offering them invites recording a
  // contradiction. Once closed: no new reports, no reason editing — the one act that still
  // makes sense is withdrawing your own record, so the form reduces to exactly that.
  const dealDone = lifecycle === 'status_rented_out' || lifecycle === 'status_sold'
  // The person who RECORDED the close, when this ad's own log can say. null = we cannot tell
  // (the close is on a sibling, or the name is masked) — never a guess.
  const closer = (lifecycleHistory || [])
    .find(h => !h.invalid && (h.value === 'status_sold' || h.value === 'status_rented_out'))?.by || null

  useEffect(() => {
    if (!propertyId) return
    let gone = false
    // Fail to an EMPTY panel, never to a missing one: if the read fails the agent must
    // still be able to record what they just learned. Losing the report is the
    // expensive outcome; showing a stale count is not.
    getMarketReports(propertyId, agentId)
      .then(d => { if (!gone) setData(d) })
      .catch(() => { if (!gone) setData({ reports: [], report_count: 0 }) })
    return () => { gone = true }
  }, [propertyId, agentId])

  if (!propertyId || !data) return null
  // ONE row per agent — their CURRENT status (owner ruling 2026-07-30: "status
  // changes chronologically; we keep the log but show only the current one").
  // Rows arrive newest-first, so the first per reporter is their latest; older
  // reports stay in the DB as the log and surface as a muted +N marker.
  const current = []
  const olderByParty = new Map()
  for (const r of (data.reports || [])) {
    const k = r.reported_by || r.party?.display_name || r.id
    if (olderByParty.has(k)) { olderByParty.set(k, olderByParty.get(k) + 1); continue }
    olderByParty.set(k, 0); current.push(r)
  }
  const mine = current.find(r => r.is_self) || null

  const submit = async (reason) => {
    if (!reason) return          // Save is disabled without one; this is the belt.
    setBusy(true); setErr(null); setKnownSaved(false)
    try {
      await recordMarketReport(propertyId, { reason, note: noteVal.trim() || null, agentId })
      setData(await getMarketReports(propertyId, agentId))
      // the known price rides the same Save, AFTER the report is safely written
      if (reason === 'deal_done_by_other' && Number(known.price) > 0) {
        try {
          await recordConfirmedDeal(propertyId, { deal: known.deal, price: Number(known.price), currency: known.currency,
            closed_on: known.date || undefined, source: 'agent_reported', evidence: noteVal.trim() || undefined })
          setKnownSaved(true)
        } catch (e) { setErr(`${t('desk_known_deal_failed')} ${e.message || ''}`.trim()) }
      }
      setOpen(false); setSaved(true); setNoteVal(''); setReason(null)
      setKnown({ deal: 'sale', price: '', currency: 'USD', date: '' })
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const doReopen = async () => {
    if (!reopenReason || !closedListingId) return
    setBusy(true); setErr(null)
    try {
      await reopenListing(closedListingId, { reason: reopenReason, scheduler_agent_id: agentId })
      setReopening(false); setReopenReason(null)
      onDealClosed?.()          // same refetch path — the projection this card reads has moved
    } catch (e) {
      // The RPC allows only the CLOSER to reopen. Say that in words, not as a status code —
      // "someone else recorded this close" is the actionable fact.
      setErr(/403|only the party/i.test(e.message) ? t('desk_reopen_not_yours') : e.message)
    } finally { setBusy(false) }
  }
  const retract = async () => {
    setBusy(true); setErr(null)
    try {
      await retractMarketReport(propertyId, agentId)
      setData(await getMarketReports(propertyId, agentId))
      setSaved(false)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  // STATUS card (owner 2026-07-30): lives in the RIGHT column above the cooperation
  // card, in the same visual language as the terms card — one row per AGENT who
  // called and recorded what the owner said ("the first thing an agent finds out").
  // Names are agency-visible (COOPERATION_VISIBILITY_SPEC §1); reports stay
  // unverified accumulate-never-delete evidence (0258).
  return (
    <div className={card}>
      {/* §2: ONE state pill for the card, in the header, right-aligned — and because it is
          amber, NOTHING inside the card is tinted. Previously every agent's reason was its
          own amber capsule, so a property four agents had called about showed four identical
          warnings and the card read as four separate alarms. */}
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <p className={SECTION_LABEL}>{t('desk_lineage_status_section')}</p>
        {/* ⚠️ THE DEAL OUTRANKS EVERYTHING ELSE ON THIS CARD (owner-caught 2026-07-30). The card
            FACE read "გაქირავებულია" from listing_state_current while this card said
            "no problem recorded — considered active": two surfaces, one property, opposite
            answers. It is the same field now, so they cannot disagree. Shown BEFORE the report
            pill because a closed deal is settled fact and a market report is an unverified
            claim — if both exist, the settled one leads. */}
        {dealDone && (
          <span className={pill('ok', { sm: true })}>
            <Icon name="flag" size={12} />
            {t(lifecycle === 'status_sold' ? 'desk_entry_closed_sold' : 'desk_entry_closed_rented')}
          </span>
        )}
        {/* §3c.1 drift 2: the header pill printed the WHOLE reason, so the same 50-character
            sentence appeared three times on one card — pill, row, and the selected radio. The
            pill's job is "this property has been reported, by N people"; the reason itself is
            the row's job. Full text stays in the tooltip. */}
        {current.length > 0 && (
          <span className={pill('warn', { sm: true })}
            title={current.map(r => `${r.party?.display_name || ''} · ${t('desk_coop_end_' + r.reason)}`).join('\n')}>
            <Icon name="alert-triangle" size={12} />
            {t('desk_status_reported_pill')}
            <span className="tabular-nums opacity-80">· {current.length}</span>
          </span>
        )}
      </div>
      {current.map(r => {
        const older = olderByParty.get(r.reported_by || r.party?.display_name || r.id) || 0
        // PRESENCE RULE (owner 2026-07-30), restated for row grammar: YOUR row IS the edit
        // door — tapping it opens the update form (reasons + comment + "available again").
        // No separate retract link. Colleagues' rows stay read-only facts.
        // §3c.1 drift 1: the NAME was truncating ("Ma…") while a 50-character reason printed
        // in full — backwards. A person's name is short and identifying; the reason is long and
        // repeated. So the name column is nowrap, capped at 132px, and the VALUE truncates.
        const body = (
          <>
            <div className="shrink-0 max-w-[132px]">
              <div className="truncate text-[13.5px] leading-[1.45] text-foreground">
                {r.party?.display_name || t('desk_terms_recorded_by_agent')}
              </div>
              <div className="text-xs leading-[1.45] text-muted-foreground tabular-nums">{timeAgo(r.observed_at)}</div>
            </div>
            <div className="min-w-0 flex-1 text-right">
              {/* §3c.1 drift 3: the SHORT reason label here; the long form (with its
                  parenthetical) lives in the tooltip. A label that wraps to three lines is
                  not a label. */}
              <div className="min-w-0 truncate text-[13.5px] leading-[1.45] text-muted-foreground"
                title={t('desk_coop_end_' + r.reason)}>
                {t('desk_market_reason_' + r.reason)}
                {/* Older reports stay in the DB as the log — the marker says the status
                    CHANGED, without stacking a row per historical value. */}
                {older > 0 && <span className={`${COUNTER} ml-1`} title={t('desk_status_log_hint')}>+{older}</span>}
              </div>
              {/* The agent's own free-text detail — "call after March", "asks for a higher
                  price now" is exactly the specificity a bare reason lacks. It belongs beside
                  the reason it qualifies, not under the name. */}
              {r.note && <div className="text-xs leading-[1.45] text-muted-foreground italic truncate" title={r.note}>„{r.note}“</div>}
            </div>
          </>
        )
        return r.is_self ? (
          <button key={r.id} type="button" disabled={busy}
            // Editing your own row pre-selects what you already said — the form opens on your
            // current answer, not blank, so "change the comment" doesn't mean "re-pick the reason".
            onClick={() => { setNoteVal(r.note || ''); setReason(r.reason); setOpen(true) }}
            title={`${t('desk_coop_end_' + r.reason)} · ${timeAgo(r.observed_at)} · ${t('edit')}`}
            className={`${ROW} w-full text-left rounded-md hover:bg-accent/40 disabled:opacity-50`}>{body}</button>
        ) : (
          <div key={r.id} className={ROW}>{body}</div>
        )
      })}
      {/* "no problem recorded — considered active" is only true when nothing terminal is
          recorded. With a closed deal it was flatly contradicting the header pill. */}
      {data.report_count === 0 && !open && !dealDone && (
        <p className="text-sm text-muted-foreground">{saved ? t('desk_market_report_saved') : t('desk_status_no_reports')}</p>
      )}
      {data.report_count === 0 && !open && dealDone && (
        <p className="text-sm text-muted-foreground">{t('desk_status_deal_closed_line')}</p>
      )}
      {/* CONTESTED (0304, stored since the reducer was written and never shown anywhere): a
          lower-trust source tried to reverse this state and lost — in practice "we recorded it
          closed, but the ad is still live on the platform". That is a real work item for
          whoever closed it, not a system detail. */}
      {dealDone && contested && (
        <p className="mt-1 text-xs leading-[1.45] rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-amber-700 dark:text-amber-400">
          <Icon name="alert-triangle" size={12} /> {t('desk_lifecycle_contested')}
        </p>
      )}
      {/* THE LOG — what this card's one pill is a reduction OF. Rendered only when the anchor
          IS the closed ad: the payload carries this ad's own history, so on a property whose
          close sits on a SIBLING the log would legitimately not contain it and would read as
          contradicting the pill above. The posted-on list badges which ad is the evidence. */}
      {(!dealDone || closedListingId === listingId) && <LifecycleHistory history={lifecycleHistory} />}
      {/* REOPEN — shown only when a deal IS recorded closed, because it is the undo of exactly
          that act. Deliberately NOT a pill among the report reasons: reopening is not a claim
          about what the owner said, it is a correction of our own record.
          Only the party who recorded the close may perform it, and when this ad's log names
          someone else we say so — "ask Tatia" is the useful answer. But that line is INFORMATION,
          not a gate: the check compares DISPLAY NAMES (roster name vs core party name), and
          hiding the door on a spelling mismatch would lock the rightful agent out of their own
          undo. Blocking a legitimate act is worse than a refused one, so the door always
          renders and the server stays the only authority — its 403 is shown in words. */}
      {dealDone && !open && closedListingId && closer && closer !== agentName && !reopening && (
        <p className="mt-2 text-xs leading-[1.45] text-muted-foreground">
          {tLog('desk_reopen_closed_by', { who: closer })}
        </p>
      )}
      {dealDone && !open && closedListingId && (reopening ? (
        <div className="mt-2 pt-2 border-t border-border/40 space-y-1">
          <p className={SECTION_LABEL}>{t('desk_reopen_title')}</p>
          <div role="radiogroup" aria-label={t('desk_reopen_title')} className="space-y-1">
            {REOPEN_REASONS.map(r => (
              <label key={r} title={t(`desk_reopen_${r}_hint`)}
                className={`flex items-center gap-2 min-h-11 md:min-h-10 px-2 rounded-md border cursor-pointer transition-colors ${
                  reopenReason === r ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}>
                <input type="radio" name={`reopen-${listingId}`} value={r} checked={reopenReason === r}
                  disabled={busy} onChange={() => setReopenReason(r)} className="accent-primary shrink-0" />
                <span className="text-[13.5px] leading-[1.45] min-w-0 truncate">{t(`desk_reopen_${r}`)}</span>
              </label>
            ))}
          </div>
          {/* The consequence of the PICKED reason, stated before the agent commits — the two
              differ in whether the closing credit survives, and that must never be a surprise. */}
          {reopenReason && (
            <p className="text-[11.5px] leading-[1.45] text-muted-foreground">{t(`desk_reopen_${reopenReason}_effect`)}</p>
          )}
          <div className="flex items-center gap-2 pt-0.5">
            <button className={btn('primary')} disabled={busy || !reopenReason} onClick={doReopen}>
              {busy ? t('desk_property_saving') : t('desk_reopen_confirm')}
            </button>
            <button className={btn('outline')} disabled={busy}
              onClick={() => { setReopening(false); setReopenReason(null) }}>{t('cancel')}</button>
          </div>
        </div>
      ) : (
        <button className={pill('add', { door: true, extra: 'mt-2' })} title={t('desk_reopen_hint')}
          disabled={busy} onClick={() => { setErr(null); setReopening(true) }}>
          <Icon name="rotate-ccw" size={12} /> {t('desk_reopen_short')}
        </button>
      ))}
      {/* Recording: no own row → dashed pill (absence affordance); own row exists →
          the CHIP is the door (presence rule), so no second pill renders. */}
      {(open ? (
        <div className="space-y-1 pt-1">
          {/* ⚠️ THIS FORM USED TO LOSE THE COMMENT (designer-caught 2026-07-30, and it was a
              DATA bug, not a layout one). Every reason was a link that called submit(r)
              directly, so the note field — rendered BELOW the reasons — was read at the
              instant of the click. Pick a reason then type the detail, i.e. the reading order
              of the form itself, and the detail silently never reached the DB. The reasons are
              a radio GROUP now and nothing writes until Save.
              §3c: 40px reason rows (44 on touch), one primary Save + outlined Cancel. */}
          <p className={SECTION_LABEL}>{t('desk_market_report_title')}</p>
          {dealDone ? (
            // Closed deal: the reasons are gone (see dealDone above) — what remains of this
            // form is the closed fact restated and, below the hairline, the retract pill.
            <p className="text-sm text-muted-foreground">{t('desk_status_deal_closed_line')}</p>
          ) : (<>
          {/* §3c.1 drift 6: unselected rows had no border, so three lines with a dot on the
              left didn't read as a CHOICE SET at all — just text. Every row is bordered; the
              selected one takes the primary border + tint, which is also what makes "one of
              these is picked" legible at a glance. */}
          <div role="radiogroup" aria-label={t('desk_market_report_title')} className="space-y-1">
            {MARKET_REASONS.map(r => (
              <label key={r} title={t('desk_coop_end_' + r)}
                className={`flex items-center gap-2 h-10 min-h-11 md:min-h-10 px-2 rounded-md border cursor-pointer transition-colors ${
                  reason === r ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}>
                <input type="radio" name={`market-reason-${propertyId}`} value={r}
                  checked={reason === r} disabled={busy}
                  onChange={() => setReason(r)} className="accent-primary shrink-0" />
                {/* Short label (drift 3) — the long form is this row's tooltip. */}
                <span className="text-[13.5px] leading-[1.45] min-w-0 truncate">{t('desk_market_reason_' + r)}</span>
              </label>
            ))}
          </div>
          {/* KNOWN DEAL — only under "someone else closed it": the price they got, if the agent learned it. Optional;
              blank = the report alone, exactly as before. */}
          {reason === 'deal_done_by_other' && (
            <div className="rounded-md border border-border/60 px-2 py-1.5 space-y-1">
              <p className="text-xs text-muted-foreground">{t('desk_known_deal_title')}</p>
              <div className="flex flex-wrap items-center gap-1.5">
                {['sale', 'rent'].map(d => (
                  <button key={d} type="button" disabled={busy} onClick={() => setKnown(k => ({ ...k, deal: d }))}
                    className={`h-8 px-2 rounded-md border text-xs ${known.deal === d ? 'border-primary bg-primary/5' : 'border-border'}`}>
                    {t(d === 'sale' ? 'desk_known_deal_sale' : 'desk_known_deal_rent')}
                  </button>
                ))}
                <input type="number" inputMode="decimal" min="0" disabled={busy}
                  className="w-28 h-8 rounded-md border border-input bg-background px-2 text-xs"
                  placeholder={t(known.deal === 'rent' ? 'desk_known_deal_rent_ph' : 'desk_known_deal_price_ph')}
                  value={known.price} onChange={e => setKnown(k => ({ ...k, price: e.target.value }))} />
                <select disabled={busy} className="h-8 rounded-md border border-input bg-background px-1 text-xs"
                  value={known.currency} onChange={e => setKnown(k => ({ ...k, currency: e.target.value }))}>
                  <option value="USD">$</option><option value="GEL">₾</option><option value="EUR">€</option>
                </select>
                <input type="date" disabled={busy} max={new Date().toISOString().slice(0, 10)}
                  className="h-8 rounded-md border border-input bg-background px-1 text-xs"
                  title={t('desk_known_deal_date')} value={known.date}
                  onChange={e => setKnown(k => ({ ...k, date: e.target.value }))} />
              </div>
              <p className="text-[11.5px] leading-[1.45] text-muted-foreground">{t('desk_known_deal_hint')}</p>
            </div>
          )}
          {/* The specific detail behind the picked reason — stored on the report's own
              `note` column. It survives now because Save is what writes. */}
          <input className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
            placeholder={t('desk_market_report_note_ph')} value={noteVal}
            onChange={e => setNoteVal(e.target.value)} />
          <div className="flex items-center gap-2 pt-0.5">
            <button className={btn('primary')} disabled={busy || !reason}
              onClick={() => submit(reason)}>{busy ? t('desk_property_saving') : t('save')}</button>
            <button className={btn('outline')} disabled={busy}
              onClick={() => { setOpen(false); setReason(null) }}>{t('cancel')}</button>
          </div>
          {/* §3c.1 drift 4: this paragraph sat ABOVE the buttons at full body size, so the
              densest text in the card came between the agent and the action they were reaching
              for. Fine print goes last, at 11.5px. */}
          <p className="text-[11.5px] leading-[1.45] text-muted-foreground">{t('desk_market_report_hint')}</p>
          </>)}
          {/* NOT reasons — these two CHANGE THE FLOW rather than describe what the owner
              said, so they sit below a hairline as pills instead of pretending to be
              radio options in the same list (§3c).
              §3c.1 drift 5: they were both `ok` (green) and their long labels filled the
              column, so they read as two equal primary actions. They are not equal: closing a
              deal is a real outcome (ok), withdrawing your own record is a neutral undo
              (dashed). `items-start` + short labels keep them shrink-to-fit. */}
          <div className="border-t border-border/40 pt-2 flex flex-wrap items-start gap-1.5">
            {/* WE closed it — a different act from reporting a rival's deal: it opens
                the deal-close confirm (final price + commission), which ends the
                cooperation as deal_closed. Same panel the meeting's status pill expands.
                GONE once the deal IS closed (owner-caught 2026-07-30: "I can initiate
                another we-closed-deal") — a door to an act that already happened is a
                double-record invitation; the header pill already states the fact. */}
            {!dealDone && (
              <button className={pill('ok', { door: true })} title={t('deal_close_we_closed')}
                disabled={busy} onClick={() => { setOpen(false); setCloseDeal(true) }}>
                {t('desk_status_we_closed_short')}
              </button>
            )}
            {/* Withdrawing your own report lives INSIDE the update form (owner 2026-07-30) —
                it is just another status the owner can state, and it clears your record. */}
            {mine && (
              <button className={pill('add', { door: true })} title={t('desk_status_available_again')}
                disabled={busy} onClick={async () => { await retract(); setOpen(false) }}>
                {t('desk_status_retract_short')}
              </button>
            )}
            {/* The reduced (closed-deal) form has no Save/Cancel row above — give it a way out. */}
            {dealDone && (
              <button className={btn('outline')} disabled={busy}
                onClick={() => { setOpen(false); setReason(null) }}>{t('cancel')}</button>
            )}
          </div>
        </div>
      ) : !mine && !dealDone ? (
        // The ONE action, last, named by its verb (§2 — a bare ＋ is not a label).
        // Gone once the deal is closed: a new report could only contradict the settled fact.
        <button title={t('desk_market_report_open')} onClick={() => setOpen(true)}
          className={pill('add', { door: true, extra: 'mt-2' })}>
          <Icon name="phone" size={12} /> {t('desk_market_report_title')}
        </button>
      ) : null)}
      {knownSaved && !open && <p className="text-xs text-muted-foreground">{t('desk_known_deal_saved')}</p>}
      {err && <p className="text-xs text-destructive">{err}</p>}
      {/* Expands IN the status card, not over it (owner 2026-07-30): this card already
          lives inside the lineage modal, so a second overlay read as "a modal opened in
          the background". Same DealClosePanel the meeting detail expands. */}
      {closeDeal && (
        <div className="mt-2 rounded-lg border border-border bg-background/60 px-3 py-2.5"
          ref={el => el && el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })}>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            <Icon name="handshake" /> {t('deal_close_title')}
          </div>
          <DealClosePanel propertyId={propertyId} listingId={listingId} lifecycle={lifecycle}
            onClose={() => setCloseDeal(false)}
            onClosed={() => { getMarketReports(propertyId, agentId).then(setData).catch(() => {}); onDealClosed?.() }} />
        </div>
      )}
    </div>
  )
}
